import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Paper,
  MenuItem,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  OutlinedInput,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { EntityStatus } from "../types";
import PageTitle from "./common/PageTitle";
import { apiService } from "../api/services/apiService";
import type { Product } from "../types";

const AddSubscription: React.FC = () => {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [productUuids, setProductUuids] = useState<string[]>([]);

  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    setProductsLoading(true);
    setProductsError(null);
    try {
      const list = await apiService.getProducts();
      setProducts(list);
    } catch (err) {
      setProductsError(err instanceof Error ? err.message : "Failed to load products");
    } finally {
      setProductsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const subscribableProducts = products.filter(
    (p) => p.status === EntityStatus.ACTIVE && p.subscribable === true,
  );

  const handleProductChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setProductUuids(typeof value === "string" ? value.split(",") : value);
  };

  const handleAdd = async () => {
    if (productUuids.length === 0) {
      setErrorMessage("Please select at least one product.");
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await apiService.createSubscription({
        productUuids,
        name: name.trim() || undefined,
        description: description.trim() || undefined,
        status: EntityStatus.ACTIVE,
      });
      setSuccessOpen(true);
      setTimeout(() => navigate("/my-subscriptions"), 800);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Failed to create subscription");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/my-subscriptions");
  };

  return (
    <Box>
      <PageTitle
        pageTitle="Add Subscription"
        pageDescription="Subscribe to one or more products in a single subscription. Your email and account are taken from your current session."
      />
      <Box sx={{ maxWidth: "lg", mx: "auto", px: { xs: 2, sm: 3 } }}>
        <Paper
          sx={{
            mt: 3,
            mb: 3,
            p: 3,
            maxWidth: 600,
            border: "1px solid #e0e0e0",
          }}
        >
          <Stack spacing={3}>
            <TextField
              label="Subscription name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              placeholder="e.g. My Payment API access"
            />
            <TextField
              label="Subscription description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Optional description for this subscription"
            />
            <FormControl fullWidth required disabled={productsLoading} error={!!productsError}>
              <InputLabel id="products-select-label">Products</InputLabel>
              <Select
                labelId="products-select-label"
                multiple
                value={productUuids}
                onChange={handleProductChange}
                input={<OutlinedInput label="Products" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((uuid) => {
                      const product = subscribableProducts.find((p) => p.uuid === uuid);
                      return (
                        <Chip key={uuid} label={product?.name ?? uuid} size="small" />
                      );
                    })}
                  </Box>
                )}
              >
                {subscribableProducts.map((p) => (
                  <MenuItem key={p.uuid} value={p.uuid}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
              {productsError && (
                <Typography component="span" variant="caption" color="error">
                  {productsError}
                </Typography>
              )}
            </FormControl>

            {productsLoading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} />
                <span>Loading products…</span>
              </Box>
            )}

            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleAdd}
                disabled={submitting || productsLoading || productUuids.length === 0}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {submitting ? "Creating…" : "Add"}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={submitting}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Cancel
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Box>

      <Snackbar open={successOpen} autoHideDuration={2000}>
        <Alert severity="success">Subscription created successfully</Alert>
      </Snackbar>
    </Box>
  );
};

export default AddSubscription;
